import { useState } from 'react';
import { Mail, Download, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { LeadCalculadora } from './types';

interface Props {
  leads: LeadCalculadora[];
  onRefresh?: () => void;
}

const ORIGEM_LABELS: Record<string, string> = {
  calculadora: 'Calculadora ROI',
  script: 'Script de Vendas',
};

const origemLabel = (origem?: string | null) =>
  ORIGEM_LABELS[origem || 'calculadora'] || origem || '—';

export const LeadsSummaryCard = ({ leads, onRefresh }: Props) => {
  const [open, setOpen] = useState(false);
  const [leadParaExcluir, setLeadParaExcluir] = useState<LeadCalculadora | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  const porOrigem = leads.reduce<Record<string, number>>((acc, l) => {
    const key = l.origem || 'calculadora';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const excluirLead = async () => {
    if (!leadParaExcluir) return;
    setExcluindo(true);
    const { error } = await supabase
      .from('leads_calculadora' as any)
      .delete()
      .eq('id', leadParaExcluir.id);
    setExcluindo(false);
    setLeadParaExcluir(null);
    if (error) {
      toast({
        title: 'Erro ao excluir lead',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    toast({ title: 'Lead excluído' });
    onRefresh?.();
  };

  const exportCSV = () => {
    if (leads.length === 0) return;
    const headers = [
      'Email',
      'Nome',
      'Empresa',
      'Origem',
      'Vendedores',
      'Ticket Médio',
      'Vendas/Mês',
      'Taxa Conversão',
      'ROI Calculado',
      'Receita Extra',
      'Data',
    ];
    const rows = leads.map((l) => [
      l.email,
      l.nome || '',
      l.empresa || '',
      origemLabel(l.origem),
      l.vendedores ?? '',
      l.ticket_medio ?? '',
      l.vendas_mes ?? '',
      l.taxa_conversao ?? '',
      l.roi_calculado ?? '',
      l.receita_extra_calculada ?? '',
      new Date(l.created_at).toLocaleDateString('pt-BR'),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setOpen(true)}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Leads
          </CardTitle>
          <CardDescription>Clique para ver, exportar e excluir</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">{leads.length}</p>
          <p className="text-sm text-muted-foreground">leads capturados</p>
          <div className="mt-3 space-y-1">
            {Object.entries(porOrigem).map(([origem, qtd]) => (
              <div key={origem} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{origemLabel(origem)}</span>
                <span className="font-semibold">{qtd}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Leads capturados ({leads.length})</span>
              <Button size="sm" variant="outline" onClick={exportCSV} disabled={leads.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </DialogTitle>
          </DialogHeader>
          {leads.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>ROI</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">{lead.email}</TableCell>
                    <TableCell>{lead.nome || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{origemLabel(lead.origem)}</Badge>
                    </TableCell>
                    <TableCell>
                      {lead.roi_calculado != null ? `${lead.roi_calculado}%` : '—'}
                    </TableCell>
                    <TableCell>{new Date(lead.created_at).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setLeadParaExcluir(lead)}
                        aria-label="Excluir lead"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum lead capturado ainda.
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!leadParaExcluir}
        onOpenChange={(o) => !o && setLeadParaExcluir(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lead?</AlertDialogTitle>
            <AlertDialogDescription>
              O lead {leadParaExcluir?.email} será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluindo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={excluirLead} disabled={excluindo}>
              {excluindo ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
